import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { Color, FontSize, MAX_WIDTH, ROUTER_PATH } from 'views/constants';
import { Spacer } from 'views/components/atoms/Spacer';
import { Typography } from 'views/components/atoms/Typography';
import { Driver } from 'views/components/atoms/Driver';
import { Icon } from 'views/components/molecules/Icon';
import { TableLink } from 'views/components/atoms/TableLink';
import { Button } from 'views/components/atoms/Button';
import { localeIds } from 'locals';
import { AppDetail } from 'views/components/organisms/AppDetail';
import { Image } from 'views/components/atoms/Image';
import { Panel } from 'views/components/molecules/Panel';
import iconImage from 'views/layouts/DistributionDetailPageLayout/images/icon.png';
import { HeaderMenu } from 'views/components/atoms/HeaderMenu';
import { Application } from 'models/Application/type';
import { ApplicationDomain } from 'models/Application';
import { DeviceDetail } from 'models/Device/type';

const ContentsWrapper = styled.div`
  text-align: initial;
  padding-bottom: 28px;
`;

const AppInfoWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const TableLinkWrapper = styled.div`
  background-color: ${Color.C6};
`;

const ButtonWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const IconWrapper = styled.div`
  > div {
    width: 64px;
    height: 64px;
  }
`;

const WaitForTesterHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  > :not(:last-child) {
    margin-right: 10px;
  }
`;

const WaitForTesterContentsWrapper = styled.div`
  > :not(:last-child) {
    margin-bottom: 4px;
  }
`;

const WaitForTesterGuideWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

type WaitForTesterProps = {
  iconSrc: string;
  targetDeviceFamily: number;
};

const WaitForTester: React.FC<WaitForTesterProps> = ({
  iconSrc,
  targetDeviceFamily,
}) => (
  <>
    <HeaderMenu>
      <WaitForTesterHeaderWrapper>
        <IconWrapper>
          <div>
            <Image src={iconSrc} />
          </div>
        </IconWrapper>
        <WaitForTesterContentsWrapper>
          <Typography bold block>
            <FormattedMessage
              id={
                localeIds.distributionDetailPageLayout.waitingForTester
                  .explanationHeader
              }
            />
          </Typography>
          <Typography block>
            <FormattedMessage id={'deviceFamilyNames.' + targetDeviceFamily}>
              {(deviceType: string): React.ReactElement => (
                <FormattedMessage
                  id={
                    localeIds.distributionDetailPageLayout.waitingForTester
                      .explanationBody
                  }
                  values={{
                    deviceType: deviceType,
                  }}
                />
              )}
            </FormattedMessage>
          </Typography>
        </WaitForTesterContentsWrapper>
      </WaitForTesterHeaderWrapper>
    </HeaderMenu>
    <Spacer />
    <WaitForTesterGuideWrapper>
      <FormattedMessage
        id={
          localeIds.distributionDetailPageLayout.waitingForTester
            .laterGuideHeader
        }
      >
        {(title: string): React.ReactElement => (
          <FormattedMessage
            id={
              localeIds.distributionDetailPageLayout.waitingForTester
                .laterGuideBody
            }
          >
            {(text: string): React.ReactElement => (
              <Panel
                title={
                  <Typography color={Color.C3} bold>
                    {title}
                  </Typography>
                }
                text={
                  <Typography size={FontSize.S} color={Color.C3}>
                    {text}
                  </Typography>
                }
                image={{
                  srcElement: <Image src={iconImage} />,
                  width: '90px',
                }}
                backgroundColor={Color.C5}
              />
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
    </WaitForTesterGuideWrapper>
    <Spacer />
  </>
);

type DistributionDetailLayoutProps = {
  deviceDetail: DeviceDetail;
  application: Application;
  displayName: string;
  notification: boolean;
  commentListPath: string;
  startInstall?: boolean;
  startOverwriteInstall?: boolean;
  userName?: string;
  isLogin?: boolean;
  notificationEmail?: string;
  onInstall(): void;
  onOverwriteInstall(): void;
  onUnsupportedOverwriteInstall(): void;
  onChangeDisplayName(currentName: string): void;
  onLeave(): void;
  onChangeNotification(notification: boolean): void;
  installReady: boolean;
  onReInstallProfile(): void;
};

export const DistributionDetailPageLayout: React.FC<DistributionDetailLayoutProps> = ({
  deviceDetail,
  application,
  displayName,
  notification,
  commentListPath,
  startInstall,
  startOverwriteInstall,
  userName,
  isLogin,
  notificationEmail,
  onInstall,
  onOverwriteInstall,
  onUnsupportedOverwriteInstall,
  onChangeDisplayName,
  onLeave,
  onChangeNotification,
  installReady,
  onReInstallProfile,
}) => (
  <ContentsWrapper>
    {application.isDeveloper ||
    ApplicationDomain.isInstallPermitted(application) ? (
      <>
        <AppDetail
          deviceDetail={deviceDetail}
          application={application}
          startInstall={startInstall}
          startOverwriteInstall={startOverwriteInstall}
          onInstall={onInstall}
          onOverwriteInstall={onOverwriteInstall}
          onUnsupportedOverwriteInstall={onUnsupportedOverwriteInstall}
          installReady={installReady}
          onReInstallProfile={onReInstallProfile}
        />
        <Spacer />
        <Driver />
      </>
    ) : (
      <WaitForTester
        iconSrc={application.iconSrc}
        targetDeviceFamily={deviceDetail.deviceFamily}
      />
    )}

    <Spacer />
    <AppInfoWrapper>
      <Icon
        type="user"
        size="small"
        label={
          <Typography bold color={Color.C3}>
            <FormattedMessage
              id={localeIds.distributionDetailPageLayout.displayName}
              values={{
                displayName: isLogin ? userName : `[${displayName}]`,
              }}
            />
          </Typography>
        }
      />
    </AppInfoWrapper>
    <Spacer />
    <Driver />

    <TableLinkWrapper>
      <AppInfoWrapper>
        <TableLink to={commentListPath}>
          <Typography bold>
            <FormattedMessage
              id={localeIds.distributionDetailPageLayout.comment}
            />
          </Typography>
        </TableLink>
      </AppInfoWrapper>
      <Driver />

      {!isLogin ? (
        <>
          <AppInfoWrapper>
            <TableLink
              to="#"
              onClick={(): void => {
                onChangeDisplayName(displayName);
              }}
            >
              <Typography bold>
                <FormattedMessage
                  id={localeIds.distributionDetailPageLayout.changeDisplayName}
                />
              </Typography>
            </TableLink>
          </AppInfoWrapper>
          <Driver />
        </>
      ) : null}

      <AppInfoWrapper>
        <TableLink to="#" onClick={onLeave}>
          <Typography bold>
            <FormattedMessage
              id={localeIds.distributionDetailPageLayout.leave}
            />
          </Typography>
        </TableLink>
      </AppInfoWrapper>
      <Driver />
      {!isLogin && (notificationEmail === '' || !notificationEmail) ? (
        <>
          <AppInfoWrapper>
            <TableLink to={ROUTER_PATH.notificationSetting}>
              <Typography bold>
                <FormattedMessage
                  id={localeIds.distributionDetailPageLayout.notification}
                />
              </Typography>
            </TableLink>
          </AppInfoWrapper>
          <Driver />
        </>
      ) : null}
    </TableLinkWrapper>
    <Spacer />

    {!isLogin && notificationEmail !== '' && notificationEmail ? (
      <ButtonWrapper>
        <Button
          type="button"
          style="secondary"
          onClick={(): void => {
            onChangeNotification(!notification);
          }}
        >
          {notification ? (
            <FormattedMessage
              id={localeIds.distributionDetailPageLayout.changeNotificationOff}
            />
          ) : (
            <FormattedMessage
              id={localeIds.distributionDetailPageLayout.changeNotificationOn}
            />
          )}
        </Button>
      </ButtonWrapper>
    ) : null}
  </ContentsWrapper>
);
